/**
 * Saved Content Page (Watch later)
 *
 * Displays videos the current user has saved to "Watch later", in a
 * 3-column grid. Mirrors Flutter's SavedContentScreen
 * (lib/presentation/screens/content/saved_content_screen.dart:159-215).
 *
 * Architecture: Page -> useSavedContent Hook -> UseCase -> Service
 *
 * Features:
 * - FlexibleHeaderList with the `saved.png` icon background
 * - 3-column grid of media thumbnails
 * - Infinite scroll pagination
 * - Reactive add/remove when saves toggle elsewhere in the app
 * - Loading, empty, and error states
 */

import { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { FlexibleHeaderList } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderList';
import { FlexibleHeaderBackground } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderBackground';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useBasePage, useGridColumnsCount } from '@/presentation/hooks';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { ContentGridItem } from '@/presentation/components/common/content/ContentGridItem';
import { ContentGridItemState } from '@/presentation/components/common/content/ContentGridItemState';
import { Navigator } from '@/presentation/routing/Navigator';
import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { useSavedContent } from './hooks/useSavedContent';

export const SavedContentPage = () => {
  const location = useLocation();
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const gridColumnsCount = useGridColumnsCount();

  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname),
  );
  const screenKey = screenKeyRef.current;

  const { medias, isLoading, isLoadingMore, hasMore, error, init, loadMore } =
    useSavedContent();

  useBasePage({
    screenKey,
    onInit: () => {
      void init();
    },
    cleanReactQueryBehaviour: 'onPop',
  });

  const handleBack = () => {
    Navigator.goBack();
  };

  const handleMediaClick = useCallback((media: UserMediaEntity) => {
    if (media.isProcessing) return;
    Navigator.navigateToContentDetails(media.id);
  }, []);

  const renderMediaItem = useCallback(
    (media: UserMediaEntity) => (
      <ContentGridItem media={media} onClick={handleMediaClick} />
    ),
    [handleMediaClick],
  );

  return (
    <FlexibleHeaderList
      qa={{ id: 'saved-content', status: isLoading ? 'loading' : 'ready' }}
      listQa={{ id: 'saved-content-list' }}
      items={medias}
      renderItem={renderMediaItem}
      getItemKey={(media) => media.id}
      gridProps={{
        columnsCount: gridColumnsCount,
        crossAxisSpacing: CONTENT_CONSTANTS.gridLayout.crossAxisSpacing,
        mainAxisSpacing: CONTENT_CONSTANTS.gridLayout.mainAxisSpacing,
        itemAspectRatio: CONTENT_CONSTANTS.gridLayout.itemAspectRatio,
        bufferRows: CONTENT_CONSTANTS.gridLayout.bufferRows,
      }}
      onLoadMore={loadMore}
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      emptyListComponent={
        <ContentGridItemState
          isLoading={isLoading}
          errorText={error ? l10n.savedContent.errorLoading : undefined}
          emptyText={l10n.savedContent.empty}
        />
      }
      backgroundColor={theme.background}
      appBarOptions={{
        title: l10n.accountInfo.watchLater,
        onLeadingTap: handleBack,
        backgroundColor: theme.background,
        flexibleSpace: {
          parallax: true,
          background: (
            <FlexibleHeaderBackground
              iconPath="/assets/images/saved.png"
              title={l10n.accountInfo.watchLater}
            />
          ),
        },
      }}
    />
  );
};
